import { useCallback, useEffect, useMemo } from 'react';
import type { OperationProgressModel } from '../../../shared/ui/progress';
import { buildMappingWorkspaceLoadingModel } from '../lib/mappingWorkspaceLoadingModel';
import { useMappingProfileApplyProgressWindow } from './useMappingProfileApplyProgressWindow';
import type { MappingProfileApplyProgressState } from './profileControllerShared';
import type { useMappingProjectReadiness } from './useMappingProjectReadiness';

interface UseWorkspaceProgressIndicatorsArgs {
  projectIdentityPending: boolean;
  profilesLoading: boolean;
  hasResolvedSelectedProfile: boolean;
  trustedProjectProfileAwaitingSessionLoad: boolean;
  resolvedProfileName: string;
  projectProfileChoiceBlocksQuickFill: boolean;
  profileProjectMatchesLoading: boolean;
  activeProjectSessionKey: string | null;
  profileConfirmedForCurrentSession: boolean;
  projectProfileGateOpen: boolean;
  profileApplyProgress: MappingProfileApplyProgressState | null;
  mappingReadiness: Pick<
    ReturnType<typeof useMappingProjectReadiness>,
    'operationProgressModel' | 'status' | 'blockReason' | 'message'
  >;
  clearProfileApplyProgress: () => void;
}

/**
 * Loading/progress surfaces for the workspace chrome: boot loading model, the
 * Quick Fill profile-restore progress card, and the profile-apply progress
 * window glue (including the auto-clear of a completed phase once the window
 * is closed).
 */
export const useWorkspaceProgressIndicators = ({
  projectIdentityPending,
  profilesLoading,
  hasResolvedSelectedProfile,
  trustedProjectProfileAwaitingSessionLoad,
  resolvedProfileName,
  projectProfileChoiceBlocksQuickFill,
  profileProjectMatchesLoading,
  activeProjectSessionKey,
  profileConfirmedForCurrentSession,
  projectProfileGateOpen,
  profileApplyProgress,
  mappingReadiness,
  clearProfileApplyProgress,
}: UseWorkspaceProgressIndicatorsArgs) => {
  const workspaceBootLoading =
    projectIdentityPending ||
    trustedProjectProfileAwaitingSessionLoad ||
    (profilesLoading && !hasResolvedSelectedProfile);
  const workspaceLoadingModel = useMemo(
    () =>
      buildMappingWorkspaceLoadingModel({
        projectIdentityPending,
        profilesLoading: profilesLoading && !hasResolvedSelectedProfile,
        restoringProjectProfile: trustedProjectProfileAwaitingSessionLoad,
        resolvedProfileName,
      }),
    [
      hasResolvedSelectedProfile,
      profilesLoading,
      projectIdentityPending,
      resolvedProfileName,
      trustedProjectProfileAwaitingSessionLoad,
    ]
  );

  const quickFillProfileRestoreProgressModel = useMemo<OperationProgressModel>(() => {
    const running =
      trustedProjectProfileAwaitingSessionLoad ||
      profilesLoading ||
      profileProjectMatchesLoading;
    const message = trustedProjectProfileAwaitingSessionLoad
      ? `Loading ${resolvedProfileName} for this Revit project`
      : 'Confirming the active project mapping profile';
    const detail = projectProfileChoiceBlocksQuickFill
      ? 'Quick Fill will open after the project profile selection is confirmed.'
      : 'Quick Fill will open after the active mapping profile is ready.';

    return {
      title: 'Preparing Quick Fill',
      message,
      detail,
      status: running ? 'running' : 'waiting',
      percent: null,
      locked: true,
    };
  }, [
    profileProjectMatchesLoading,
    profilesLoading,
    projectProfileChoiceBlocksQuickFill,
    resolvedProfileName,
    trustedProjectProfileAwaitingSessionLoad,
  ]);

  const profileApplyWindow = useMappingProfileApplyProgressWindow({
    profileApplyProgress,
    readinessProgressModel: mappingReadiness.operationProgressModel,
    readinessStatus: mappingReadiness.status,
    readinessBlockReason: mappingReadiness.blockReason,
    readinessMessage: mappingReadiness.message,
    resolvedProfileName,
    profileSessionPending:
      Boolean(activeProjectSessionKey) &&
      profileApplyProgress?.phase === 'complete' &&
      !profileConfirmedForCurrentSession,
    projectProfileGateOpen,
  });
  // A trusted project profile is already being restored before the profile
  // controller publishes its first progress phase. Keep that interval on the
  // same high-priority surface as the subsequent apply operation so Quick Fill
  // cannot briefly replace it with its lower-priority preparation window.
  const trustedProfileRestoreWindowModel = useMemo<OperationProgressModel | null>(() => {
    if (!trustedProjectProfileAwaitingSessionLoad) {
      return null;
    }

    return {
      title: 'Applying mapping profile',
      message: `Loading ${resolvedProfileName} for this Revit project`,
      detail: 'Quick Fill will open after the active mapping profile is ready.',
      status: 'running',
      percent: null,
      locked: true,
    };
  }, [resolvedProfileName, trustedProjectProfileAwaitingSessionLoad]);
  const profileApplyWindowModel =
    profileApplyWindow.model ?? trustedProfileRestoreWindowModel;
  const profileApplyWindowOpen =
    profileApplyWindow.open || trustedProfileRestoreWindowModel !== null;
  const handleProfileApplyProgressClose = useCallback(() => {
    clearProfileApplyProgress();
    profileApplyWindow.clearSession();
  }, [clearProfileApplyProgress, profileApplyWindow]);
  useEffect(() => {
    if (profileApplyProgress?.phase !== 'complete') {
      return;
    }

    if (profileApplyWindow.open) {
      return;
    }

    clearProfileApplyProgress();
  }, [
    clearProfileApplyProgress,
    profileApplyWindow.open,
    profileApplyProgress?.phase,
  ]);

  return {
    workspaceBootLoading,
    workspaceLoadingModel,
    quickFillProfileRestoreProgressModel,
    profileApplyWindowOpen,
    profileApplyWindowModel,
    handleProfileApplyProgressClose,
  };
};
